// services/api/strangerApi.js
import axiosInstance from "utils/axiosInstance";

const strangerApi = {
  /**
   * Fetches all cameras
   * @returns {Promise<Array>} A promise that resolves to an array of camera objects
   */
  getCameras: async () => {
    const response = await axiosInstance.get("/admin/cameras");
    return response?.data?.data?.cameras || [];
  },

  /**
   * Fetches all employees (including strangers)
   * @returns {Promise<Array>} A promise that resolves to an array of employee objects
   */
  getEmployees: async () => {
    const response = await axiosInstance.get("/employee/getall");
    return response?.data?.data?.employees || [];
  },

  /**
   * Updates a stranger's status
   * @param {string} strangerId - The ID of the stranger to update
   * @param {string} status - The new status
   * @returns {Promise<Object>} A promise that resolves to the response data
   */
  updateStrangerStatus: async (strangerId, status) => {
    const employeeId = strangerId.startsWith('STR-')
      ? parseInt(strangerId.substring(4))
      : parseInt(strangerId);
    
    const response = await axiosInstance.put(`/employee/update/status/${employeeId}`, { status });
    return response.data;
  },

  /**
   * Updates multiple strangers' status in bulk
   * @param {Array<string>} strangerIds - Array of stranger IDs to update
   * @param {string} status - The new status
   * @returns {Promise<Object>} A promise that resolves to the response data
   */
  bulkUpdateStrangerStatus: async (strangerIds, status) => {
    const employeeIds = strangerIds.map(id => {
      return id.startsWith('STR-')
        ? parseInt(id.substring(4))
        : parseInt(id);
    });

    const response = await axiosInstance.put('/employee/update/bulk-status', {
      employee_ids: employeeIds,
      status
    });
    return response.data;
  },

  /**
   * Fetches employee attendance summary
   * @param {string} employeeId - The ID of the employee
   * @returns {Promise<Object>} A promise that resolves to the attendance summary
   */
  getEmployeeAttendanceSummary: async (employeeId) => {
    const response = await axiosInstance.get(`employee/get-summary/${employeeId}`);
    return response.data;
  },

  /**
   * Fetches employee attendance records
   * @param {string} employeeId - The ID of the employee
   * @param {string} filter - The time filter
   * @returns {Promise<Array>} A promise that resolves to an array of attendance records
   */
  getEmployeeAttendanceRecords: async (employeeId, filter) => {
    const response = await axiosInstance.get(
      `employee/getbyid/${employeeId}?filter=${filter}`
    );
    return response?.data?.records?.map((r) => ({
      ...r,
      checkIn: r.checkIn ? new Date(r.checkIn) : null,
      checkOut: r.checkOut ? new Date(r.checkOut) : null,
    })) || [];
  },
  switchStrangerToEmployee: async (data) => {
    const response = await axiosInstance.post('/employee/switch-stranger-to-employee', data);
    return response.data;
  },

  /**
   * Gets list of employees for switching a stranger
   * @param {string} search - Search term to filter employees
   * @returns {Promise<Object>} A promise that resolves to the response data
   */
  getEmployeesForSwitch: async (search = '') => {
    const response = await axiosInstance.get(`/employee/get-employees-for-switch?search=${search}`);
    return response.data;
  },
};

export default strangerApi;